import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import isEmpty from 'lodash/isEmpty';
import AssignmentIcon from '@material-ui/icons/Assignment';
import CheckIcon from '@material-ui/icons/Check';
import Actions from 'actions';
import Selectors from 'selectors';
import { isProduction } from 'utils/config';
import { Loading, Card, Button, ErrorContainer } from 'components/UI';
import { iconNext } from 'assets';
import {
  CREATIVE_CONDITIONAL_FIELD_KEYS,
  createOnboardingUpdateConfig,
  createPeriodicPreviewProfile,
  getMissingFieldKeys,
  getOnboardingStatus,
  getVisibleSteps,
  isCreativeProfile,
  isProfileComplete,
  ONBOARDING_STATUSES,
} from './config';
import {
  fromSelectValues,
  fromSelectValuesToIdObjects,
  toSelectValues,
  USER_CLASSIFICATION_TYPES,
} from './options';
import STEP_SCHEMAS from './validation';
import WizardProgress from './components/WizardProgress';
import StepHeading from './components/StepHeading';
import { AboutYouStep, ConnectStep, WorkStep } from './steps';

const scrollToWizardTop = () => {
  const scrollContainer = document.getElementById('scrollableContent');
  if (scrollContainer) scrollContainer.scrollTo(0, 0);
  else window.scrollTo(0, 0);
};

const servicesInitialValue = (array) => {
  if (isEmpty(array) || typeof array[0] === 'string') {
    return [{ name: '', description: '' }];
  }
  return array;
};

const experiencesInitialValue = (array) => {
  if (!isEmpty(array)) {
    return array.map((experience) =>
      experience.year_end ? experience : { ...experience, year_end: ' ' }
    );
  }

  return [
    {
      company: '',
      company_url: '',
      position: '',
      year_start: '',
      year_end: '',
    },
  ];
};

const Onboarding = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { search, state: navigationState } = location;
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(null);
  const [hasFetchedProfile, setHasFetchedProfile] = useState(false);
  const [sessionFieldKeys, setSessionFieldKeys] = useState(null);
  const [onboardingEntryReason] = useState(() => {
    if (navigationState?.enteredFromLogin) return 'login';
    if (navigationState?.onboardingRequired) return 'navigation';
    return null;
  });
  const hasObservedProfileRequest = useRef(false);

  useEffect(() => {
    if (!onboardingEntryReason) return;

    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: null }
    );
  }, [
    location.hash,
    location.pathname,
    location.search,
    navigate,
    onboardingEntryReason,
  ]);

  useEffect(() => {
    dispatch(Actions.fetchProfile());
    dispatch(Actions.fetchLanguages());
    dispatch(Actions.fetchCountries());
    Object.values(USER_CLASSIFICATION_TYPES).forEach((type) => {
      dispatch(Actions.fetchUserClassifications({ type }));
    });
  }, [dispatch]);

  useEffect(() => {
    scrollToWizardTop();
  }, [activeStep]);

  const isLoading = useSelector(
    Selectors.createLoadingSelector([
      Actions.FETCH_PROFILE_REQUEST,
      Actions.UPDATE_USER_REQUEST,
      Actions.FETCH_LANGUAGES_REQUEST,
    ])
  );

  const storedUserProfile = useSelector(Selectors.userProfile);
  const isPeriodicPreview =
    !isProduction && new URLSearchParams(search).get('preview') === 'periodic';
  const userProfile = useMemo(
    () =>
      isPeriodicPreview
        ? createPeriodicPreviewProfile(storedUserProfile)
        : storedUserProfile,
    [isPeriodicPreview, storedUserProfile]
  );
  const isLoadingProfile = useSelector(
    Selectors.createLoadingSelector([Actions.FETCH_PROFILE_REQUEST])
  );
  const languagesOptions = useSelector(Selectors.languages);
  const countries = useSelector(Selectors.countries);
  const userClassifications = useSelector(Selectors.userClassifications);
  const isLoadingClassifications = useSelector(
    Selectors.isLoadingUserClassifications
  );
  const isLoadingTargetMarkets = useSelector(
    Selectors.createLoadingSelector([Actions.FETCH_COUNTRIES_REQUEST])
  );
  const classificationOptions = useMemo(() => {
    const apiOptions = Object.entries(USER_CLASSIFICATION_TYPES).reduce(
      (options, [fieldName, classificationType]) => ({
        ...options,
        [fieldName]: toSelectValues(
          userClassifications[classificationType]?.data || []
        ),
      }),
      {}
    );

    return {
      ...apiOptions,
      target_markets: toSelectValues(countries),
    };
  }, [countries, userClassifications]);
  const onboardingStatus = getOnboardingStatus(userProfile);
  const isPeriodicReview = onboardingStatus === ONBOARDING_STATUSES.DUE;

  useEffect(() => {
    if (isLoadingProfile) {
      hasObservedProfileRequest.current = true;
      return;
    }

    if (hasObservedProfileRequest.current) setHasFetchedProfile(true);
  }, [isLoadingProfile]);

  useEffect(() => {
    if (
      !hasFetchedProfile ||
      isEmpty(userProfile) ||
      sessionFieldKeys !== null
    ) {
      return;
    }

    setSessionFieldKeys(getMissingFieldKeys(userProfile, onboardingStatus));
  }, [hasFetchedProfile, onboardingStatus, sessionFieldKeys, userProfile]);

  const visibleSteps = getVisibleSteps(sessionFieldKeys || []);
  const currentStep = visibleSteps[activeStep];
  const isSinglePage = visibleSteps.length === 1;
  const isLastStep = activeStep === visibleSteps.length - 1;
  let submitLabel = 'Continue';

  if (isSinglePage || (isLastStep && isPeriodicReview)) {
    submitLabel = 'Update profile';
  } else if (isLastStep) {
    submitLabel = 'Complete onboarding';
  }

  const {
    image_url: avatarUrl,
    country,
    city,
    industry_since: industrySince,
    member_types: memberTypes,
    biography,
    languages,
    rep_artists: repArtist,
    rep_events: repEvents,
    rep_venues: repVenues,
    users_services: userServices,
    users_experiences: userExperiences,
    facebook,
    linked_in: linkedIn,
    instagram,
    spotify_playlists: spotifyPlaylists,
    youtube_playlists: youtubePlaylists,
    industry_areas: industryAreas,
    target_markets: targetMarkets,
    professional_goals: professionalGoals,
    professional_skills: professionalSkills,
    professional_roles: professionalRoles,
    interest_topics: interestTopics,
    music_genres: musicGenres,
    event_types: eventTypes,
    event_formats: eventFormats,
    event_attend_locations: eventAttendLocations,
    chat_notification: chatNotification,
  } = userProfile || {};

  const initialValues = {
    avatarUrl: avatarUrl || '',
    country: country?.name || country || '',
    city: city?.name || city || '',
    industry_since: industrySince || '',
    member_types: toSelectValues(memberTypes),
    biography: biography || '',
    languages:
      languages?.map(({ name }) => ({
        label: name,
        value: name,
      })) || [],
    users_services: servicesInitialValue(userServices),
    users_experiences: experiencesInitialValue(userExperiences),
    rep_artists: toSelectValues(repArtist?.filter(i => i.trim() !== '')),
    rep_events: toSelectValues(repEvents?.filter(i => i.trim() !== '')),
    rep_venues: toSelectValues(repVenues?.filter(i => i.trim() !== '')),
    facebook: facebook || '',
    linked_in: linkedIn || '',
    instagram: instagram || '',
    spotify_playlists: isEmpty(spotifyPlaylists) ? [''] : spotifyPlaylists,
    youtube_playlists: isEmpty(youtubePlaylists) ? [''] : youtubePlaylists,
    industry_areas: toSelectValues(industryAreas),
    target_markets: toSelectValues(targetMarkets),
    professional_goals: toSelectValues(professionalGoals),
    professional_skills: toSelectValues(professionalSkills),
    professional_roles: toSelectValues(professionalRoles),
    interest_topics: toSelectValues(interestTopics),
    music_genres: toSelectValues(musicGenres),
    event_types: toSelectValues(eventTypes),
    event_formats: toSelectValues(eventFormats),
    event_attend_locations: toSelectValues(eventAttendLocations),
    chat_notification: chatNotification || false,
  };

  const handleIndustryAreasChange = useCallback(
    (selectedIndustryAreas = []) => {
      if (!isPeriodicReview) return;

      const currentProfile = {
        ...userProfile,
        industry_areas: selectedIndustryAreas,
      };

      setSessionFieldKeys((currentFieldKeys) => {
        if (!currentFieldKeys) return currentFieldKeys;

        const baseFieldKeys = currentFieldKeys.filter(
          (key) => !CREATIVE_CONDITIONAL_FIELD_KEYS.includes(key)
        );

        if (!isCreativeProfile(currentProfile)) return baseFieldKeys;

        const missingCreativeFieldKeys = getMissingFieldKeys(
          currentProfile,
          ONBOARDING_STATUSES.DUE
        ).filter((key) => CREATIVE_CONDITIONAL_FIELD_KEYS.includes(key));

        return [...new Set([...baseFieldKeys, ...missingCreativeFieldKeys])];
      });
    },
    [isPeriodicReview, userProfile]
  );

  function handleOnSubmit(formValues, actions) {
    setError(null);

    if (activeStep < visibleSteps.length - 1) {
      setActiveStep((step) => step + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
      return;
    }

    const {
      languages: selectedLanguages,
      rep_artists: repArtists,
      rep_events: selectedRepEvents,
      rep_venues: selectedRepVenues,
      users_services: selectedServices,
      users_experiences: selectedExperiences,
      member_types: selectedMemberTypes,
      industry_areas: selectedIndustryAreas,
      target_markets: selectedTargetMarkets,
      professional_goals: selectedProfessionalGoals,
      professional_skills: selectedProfessionalSkills,
      professional_roles: selectedProfessionalRoles,
      interest_topics: selectedInterestTopics,
      music_genres: selectedMusicGenres,
      event_types: selectedEventTypes,
      event_formats: selectedEventFormats,
      event_attend_locations: selectedEventAttendLocations,
    } = formValues;

    const normalizedValues = {
      ...userProfile,
      ...formValues,
      image_url: formValues.avatarUrl,
      member_types: fromSelectValues(selectedMemberTypes),
      languages: fromSelectValues(selectedLanguages),
      industry_areas: selectedIndustryAreas,
      target_markets: fromSelectValues(selectedTargetMarkets),
      professional_goals: fromSelectValues(selectedProfessionalGoals),
      professional_skills: fromSelectValues(selectedProfessionalSkills),
      professional_roles: fromSelectValues(selectedProfessionalRoles),
      interest_topics: fromSelectValues(selectedInterestTopics),
      music_genres: fromSelectValues(selectedMusicGenres),
      event_types: fromSelectValues(selectedEventTypes),
      event_formats: fromSelectValues(selectedEventFormats),
      event_attend_locations: fromSelectValues(selectedEventAttendLocations),
      rep_artists: fromSelectValues(repArtists),
      rep_events: fromSelectValues(selectedRepEvents),
      rep_venues: fromSelectValues(selectedRepVenues),
    };
    const completedProfile = isProfileComplete(normalizedValues);

    const parsedFormValue = {
      ...formValues,
      ...createOnboardingUpdateConfig(completedProfile),
      member_type_ids: fromSelectValues(selectedMemberTypes),
      languages: fromSelectValues(selectedLanguages),
      industry_areas: fromSelectValuesToIdObjects(selectedIndustryAreas),
      target_markets: fromSelectValuesToIdObjects(selectedTargetMarkets),
      professional_goals: fromSelectValuesToIdObjects(
        selectedProfessionalGoals
      ),
      professional_skills: fromSelectValuesToIdObjects(
        selectedProfessionalSkills
      ),
      professional_roles: fromSelectValuesToIdObjects(
        selectedProfessionalRoles
      ),
      interest_topics: fromSelectValuesToIdObjects(selectedInterestTopics),
      music_genres: fromSelectValuesToIdObjects(selectedMusicGenres),
      event_types: fromSelectValuesToIdObjects(selectedEventTypes),
      event_formats: fromSelectValuesToIdObjects(selectedEventFormats),
      event_attend_locations: fromSelectValuesToIdObjects(
        selectedEventAttendLocations
      ),
      rep_artists: fromSelectValues(repArtists),
      rep_events: fromSelectValues(selectedRepEvents),
      rep_venues: fromSelectValues(selectedRepVenues),
      users_services_attributes: selectedServices,
      users_experiences_attributes: selectedExperiences,
      ...(completedProfile && { completed_profile: true }),
    };

    // if (isPeriodicPreview) {
    //   console.log('parsedFormValue', parsedFormValue)
    //   // actions.setSubmitting(false);
    //   // navigate('/dashboard', { replace: true });
    //   return;
    // }

    dispatch(
      Actions.updateUser(parsedFormValue, {
        success: () => {
          actions.setSubmitting(false);

          if (completedProfile) {
            navigate('/dashboard', { replace: true });
            return;
          }

          dispatch(
            Actions.showDialog({
              title: 'You are all set!',
              isSuccess: true,
              hideButtons: true,
              content: (
                <div className="flex flex-col items-center">
                  <div className="mb-6 text-center">
                    Your profile essentials are complete. You can add more
                    details at any time.
                  </div>
                  <div className="flex flex-col w-full space-y-4">
                    <Button
                      label="Go to Dashboard"
                      className="w-full h-12 px-4"
                      onClick={() => {
                        dispatch(Actions.hideDialog());
                        navigate('/dashboard', { replace: true });
                      }}
                      isPill
                    />
                    <Button
                      label="Add More Profile Details"
                      className="w-full h-12 px-4"
                      theme="plain"
                      outline
                      onClick={() => {
                        dispatch(Actions.hideDialog());
                        navigate('/profile', { replace: true });
                      }}
                      isPill
                    />
                  </div>
                </div>
              ),
            })
          );
        },
        failure: (submitError) => {
          setError(
            typeof submitError === 'string'
              ? submitError
              : 'Failed to setup account'
          );
          actions.setSubmitting(false);
        },
      })
    );
  }

  const renderStep = () => {
    switch (currentStep?.key) {
      case 'about_you':
        return (
          <AboutYouStep
            country={initialValues.country}
            fieldKeys={currentStep.fieldKeys}
            isLoading={isLoading}
            languagesOptions={languagesOptions}
          />
        );
      case 'work':
        return (
          <WorkStep
            classificationOptions={classificationOptions}
            fieldKeys={currentStep.fieldKeys}
            isLoadingClassifications={isLoadingClassifications}
            isLoadingTargetMarkets={isLoadingTargetMarkets}
            onIndustryAreasChange={handleIndustryAreasChange}
          />
        );
      case 'connect':
        return (
          <ConnectStep
            classificationOptions={classificationOptions}
            fieldKeys={currentStep.fieldKeys}
            isLoadingClassifications={isLoadingClassifications}
          />
        );
      default:
        return null;
    }
  };

  const handleCompleteWithoutChanges = () => {
    if (onboardingStatus !== ONBOARDING_STATUSES.REQUIRED) {
      navigate('/dashboard', { replace: true });
      return;
    }

    dispatch(
      Actions.updateUser(
        isProfileComplete(userProfile) ? { completed_profile: true } : {},
        {
          success: () => navigate('/dashboard', { replace: true }),
          failure: (submitError) =>
            setError(
              typeof submitError === 'string'
                ? submitError
                : 'Failed to complete onboarding'
            ),
        }
      )
    );
  };

  if (!hasFetchedProfile || (isLoading && isEmpty(userProfile))) {
    return <Loading className="fixed left-0 top-0 bottom-0 right-0 z-50" />;
  }

  if (sessionFieldKeys === null) return null;

  if (visibleSteps.length === 0) {
    return (
      <div className="bg-alabaster min-h-screen py-8 md:py-12">
        <div className="container mx-auto px-4 sm:px-6 max-w-140">
          <Card className="border-t-8 border-easter px-5 py-10 sm:p-12 text-center">
            <div className="bg-teal-100 rounded-full p-3 mb-4 text-easter inline-flex">
              <CheckIcon fontSize="large" />
            </div>
            <h1 className="text-2lg text-ebony mb-3">
              Your profile is up to date
            </h1>
            <p className="text-dusty text-sm mb-8">
              There are no missing profile details to review right now.
            </p>
            <ErrorContainer error={error} />
            <Button
              label="Go to Dashboard"
              isLoading={isLoading}
              loadInButton
              onClick={handleCompleteWithoutChanges}
            />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-alabaster min-h-screen py-8 md:py-12">
      <div className="container mx-auto px-4 sm:px-6 max-w-200">
        {isPeriodicReview && (
          <div
            className="bg-squeeze border-l-4 border-easter rounded px-5 py-4 mb-6 text-sm text-ebony shadow"
            role="status"
          >
            <p className="font-semibold mb-1">
              Please review your profile before continuing
            </p>
            <p className="text-dusty">
              All fields in this review are optional. Update anything that has
              changed, or continue through the review without adding new
              information.
            </p>
          </div>
        )}
        {onboardingEntryReason === 'navigation' && !isPeriodicReview && (
          <div
            className="bg-pippin border-l-4 border-valencia rounded px-5 py-4 mb-6 text-sm text-ebony shadow"
            role="alert"
          >
            <p className="font-semibold mb-1">
              Please complete onboarding before continuing
            </p>
            <p className="text-dusty">
              The page you selected will be available after you finish the
              required profile information below.
            </p>
          </div>
        )}
        {isPeriodicPreview && (
          <div className="bg-teal-100 border border-easter text-ebony rounded px-4 py-3 mb-6 text-sm text-center">
            Periodic onboarding preview — mock data only. Changes will not be
            saved.
          </div>
        )}
        <div className="text-center mb-8">
          <div className="bg-teal-100 rounded-full p-3 mb-4 text-easter inline-flex">
            <AssignmentIcon fontSize="large" />
          </div>
          <h1 className="text-2lg md:text-3lg text-ebony mb-2">
            {isPeriodicReview ? 'Review your profile' : 'Complete your profile'}
          </h1>
          <p className="text-dusty text-sm max-w-140 mx-auto">
            {isPeriodicReview
              ? 'Keep your information current and complete any details still missing.'
              : 'Add the essentials that help you get discovered and make better connections.'}
          </p>
          {!isPeriodicReview && (
            <div className="bg-white border-l-4 border-easter rounded shadow px-5 py-4 mt-5 max-w-140 mx-auto text-left">
              <p className="text-xs uppercase tracking-wider text-easter font-semibold mb-1">
                Required before you continue
              </p>
              <p className="text-sm text-ebony mb-1">
                Complete the required profile details to access the rest of Gig
                Life Pro.
              </p>
              <p className="text-xs text-dusty">
                These details improve member matching, recommendations, and the
                opportunities we can show you.
              </p>
            </div>
          )}
        </div>

        <Card className="border-t-8 border-easter px-5 py-8 sm:p-10">
          {!isSinglePage && (
            <WizardProgress activeStep={activeStep} steps={visibleSteps} />
          )}
          <Formik
            initialValues={initialValues}
            validationSchema={STEP_SCHEMAS[currentStep.key]}
            onSubmit={handleOnSubmit}
            enableReinitialize
          >
            {({ isSubmitting, setTouched }) => (
              <Form>
                <StepHeading
                  isPeriodicReview={isPeriodicReview}
                  isSinglePage={isSinglePage}
                  step={currentStep}
                />
                <div className="min-h-64">{renderStep()}</div>
                <ErrorContainer error={error} />
                <div className="border-t border-alto mt-8 pt-6 flex items-center justify-between">
                  <Button
                    type="button"
                    label="Back"
                    theme="plain"
                    outline
                    className={
                      isSinglePage || activeStep === 0 ? 'invisible' : null
                    }
                    onClick={() => {
                      setError(null);
                      setTouched({});
                      setActiveStep((step) => Math.max(step - 1, 0));
                    }}
                  />
                  <Button
                    type="submit"
                    className="text-xs md:text-base"
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                    loadInButton
                    label={
                      <div className="flex items-center">
                        {submitLabel}
                        <img
                          alt="next"
                          src={iconNext.default}
                          className="h-4 mt-1 ml-4"
                        />
                      </div>
                    }
                  />
                </div>
              </Form>
            )}
          </Formik>
        </Card>
        <p className="text-center text-xs text-dusty mt-6">
          Your information can be updated later from My Profile.
        </p>
      </div>
    </div>
  );
};

export default Onboarding;
